import { useState, useEffect, useCallback } from 'react';
import type { AgentLocation } from '@/contracts/brokerage';

const STORAGE_KEY = 'agent_location';

/**
 * Hook for managing agent location state
 * Persists to localStorage and supports geolocation
 */
export function useAgentLocation() {
  const [location, setLocationState] = useState<AgentLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setLocationState(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load agent location from storage:', e);
    }
  }, []);

  // Save to localStorage whenever location changes
  const setLocation = useCallback((newLocation: AgentLocation | null) => {
    setLocationState(newLocation);
    if (newLocation) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocation));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Set location manually (state selector)
  const setManualLocation = useCallback((state: string, city?: string) => {
    setLocation({
      state,
      city,
      source: 'manual',
      radius_miles: 50,
    });
    setError(null);
  }, [setLocation]);

  // Get location from browser geolocation
  const requestGeolocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode to get state
      // Using a simple approach - in production you'd use a proper geocoding service
      // For now, we'll just set coords and let the user confirm/select state
      setLocation({
        state: '', // Will need user to confirm
        lat: latitude,
        lng: longitude,
        source: 'geolocation',
        radius_miles: 25, // Tighter radius when using exact location
      });

      // Try to get state from coordinates using nominatim (free, no API key)
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        );
        const data = await response.json();
        
        if (data.address?.state) {
          // Map full state name to abbreviation
          const stateAbbrev = getStateAbbreviation(data.address.state);
          if (stateAbbrev) {
            setLocation({
              state: stateAbbrev,
              city: data.address.city || data.address.town || data.address.village,
              lat: latitude,
              lng: longitude,
              source: 'geolocation',
              radius_miles: 25,
            });
          }
        }
      } catch (geocodeError) {
        console.warn('Reverse geocoding failed, coordinates still set:', geocodeError);
      }
    } catch (err) {
      const geoError = err as GeolocationPositionError;
      switch (geoError.code) {
        case geoError.PERMISSION_DENIED:
          setError('Location permission denied. Please select your state manually.');
          break;
        case geoError.POSITION_UNAVAILABLE:
          setError('Location unavailable. Please select your state manually.');
          break;
        case geoError.TIMEOUT:
          setError('Location request timed out. Please select your state manually.');
          break;
        default:
          setError('Failed to get location. Please select your state manually.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [setLocation]);

  // Clear location
  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
  }, [setLocation]);

  // Update radius
  const setRadius = useCallback((radius: number) => {
    if (location) {
      setLocation({ ...location, radius_miles: radius });
    }
  }, [location, setLocation]);

  return {
    location,
    isLoading,
    error,
    setManualLocation,
    requestGeolocation,
    clearLocation,
    setRadius,
    hasLocation: !!location?.state,
  };
}

// Helper to convert full state name to abbreviation
function getStateAbbreviation(fullName: string): string | null {
  const stateMap: Record<string, string> = {
    'Alabama': 'AL',
    'Alaska': 'AK',
    'Arizona': 'AZ',
    'Arkansas': 'AR',
    'California': 'CA',
    'Colorado': 'CO',
    'Connecticut': 'CT',
    'Delaware': 'DE',
    'Florida': 'FL',
    'Georgia': 'GA',
    'Hawaii': 'HI',
    'Idaho': 'ID',
    'Illinois': 'IL',
    'Indiana': 'IN',
    'Iowa': 'IA',
    'Kansas': 'KS',
    'Kentucky': 'KY',
    'Louisiana': 'LA',
    'Maine': 'ME',
    'Maryland': 'MD',
    'Massachusetts': 'MA',
    'Michigan': 'MI',
    'Minnesota': 'MN',
    'Mississippi': 'MS',
    'Missouri': 'MO',
    'Montana': 'MT',
    'Nebraska': 'NE',
    'Nevada': 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    'Ohio': 'OH',
    'Oklahoma': 'OK',
    'Oregon': 'OR',
    'Pennsylvania': 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    'Tennessee': 'TN',
    'Texas': 'TX',
    'Utah': 'UT',
    'Vermont': 'VT',
    'Virginia': 'VA',
    'Washington': 'WA',
    'West Virginia': 'WV',
    'Wisconsin': 'WI',
    'Wyoming': 'WY',
    'District of Columbia': 'DC',
  };
  
  return stateMap[fullName] || null;
}
